import { apiSlice } from "./apiSlice";

/**
 * ZeroTyping Teams API (/api/org on the Node backend).
 *
 * Tags: "OrgMe" = the caller's own seat, "Org" = Team page data (/mine),
 * "OrgBilling" = card, invoices, next payment, "OrgInvites" = invites
 * addressed to the caller (to join a team). Each mutation invalidates what
 * it changes, so the page refetches with one call.
 *
 * Errors come back as { status, data: { success: false, code, message } };
 * use orgErrorMessage() to show them.
 */

const withTags = apiSlice.enhanceEndpoints({ addTagTypes: ["OrgMe", "Org", "OrgBilling", "OrgInvites"] });

export const orgApi = withTags.injectEndpoints({
  endpoints: (builder) => ({
    // Any signed-in user: { membership: { orgId, orgName, role, canManage, … } | null }
    getMyMembership: builder.query({
      query: () => ({ url: "/org/me", method: "GET" }),
      providesTags: ["OrgMe"],
    }),

    // Owners and admins: { org, members, invites }
    getMyOrg: builder.query({
      query: () => ({ url: "/org/mine", method: "GET" }),
      providesTags: ["Org"],
    }),

    getOrgBilling: builder.query({
      query: () => ({ url: "/org/billing", method: "GET" }),
      providesTags: ["OrgBilling"],
    }),

    previewSeats: builder.query({
      query: (seats) => ({ url: "/org/seats/preview", method: "GET", params: { seats } }),
      keepUnusedDataFor: 0,
    }),

    createTeamSubscriptionSession: builder.mutation({
      query: (body) => ({ url: "/org/create-subscription-session", method: "POST", body }),
    }),

    // A team on its 7-day free trial, no card: { org: { id, name, status, trialEndsAt } }
    createTeamTrial: builder.mutation({
      query: (orgName) => ({ url: "/org/create-trial", method: "POST", body: { orgName } }),
      invalidatesTags: ["OrgMe", "Org", "OrgInvites"],
    }),

    // Owners and admins: Overview page (plan, seats, last 7 days, recent activity).
    getOrgOverview: builder.query({
      query: () => ({ url: "/org/overview", method: "GET" }),
      providesTags: ["Org"],
    }),
    // Owners and admins: Team usage page (minutes per person, 7 and 30 days).
    getOrgUsage: builder.query({
      query: () => ({ url: "/org/usage", method: "GET" }),
      providesTags: ["Org"],
    }),

    // Owner: Add billing (trial) or Reactivate (paused team).
    getBillingSetup: builder.query({
      query: () => ({ url: "/org/billing/setup", method: "GET" }),
      providesTags: ["OrgBilling"],
    }),
    authorizeBilling: builder.mutation({
      query: (body) => ({ url: "/org/billing/authorize", method: "POST", body }),
    }),
    confirmBilling: builder.mutation({
      query: (intentId) => ({ url: "/org/billing/confirm", method: "POST", body: { intentId } }),
      invalidatesTags: ["OrgMe", "Org", "OrgBilling"],
    }),

    createInvite: builder.mutation({
      query: (body) => ({ url: "/org/invites", method: "POST", body }),
      invalidatesTags: ["Org"],
    }),
    resendInvite: builder.mutation({
      query: (id) => ({ url: `/org/invites/${id}/resend`, method: "POST" }),
      invalidatesTags: ["Org"],
    }),
    revokeInvite: builder.mutation({
      query: (id) => ({ url: `/org/invites/${id}/revoke`, method: "POST" }),
      invalidatesTags: ["Org"],
    }),
    reinvite: builder.mutation({
      query: (id) => ({ url: `/org/invites/${id}/reinvite`, method: "POST" }),
      invalidatesTags: ["Org"],
    }),
    hideInvite: builder.mutation({
      query: (id) => ({ url: `/org/invites/${id}/hide`, method: "POST" }),
      invalidatesTags: ["Org"],
    }),

    changeMemberRole: builder.mutation({
      query: ({ userId, role }) => ({ url: `/org/members/${userId}/role`, method: "POST", body: { role } }),
      invalidatesTags: ["Org"],
    }),
    removeMember: builder.mutation({
      query: (userId) => ({ url: `/org/members/${userId}/remove`, method: "POST" }),
      invalidatesTags: ["Org"],
    }),

    updateSeats: builder.mutation({
      query: (seats) => ({ url: "/org/seats", method: "POST", body: { seats } }),
      invalidatesTags: ["Org", "OrgBilling"],
    }),
    // After paying for added seats on the checkout page: apply them now.
    confirmSeatPayment: builder.mutation({
      query: (paymentIntentId) => ({ url: "/org/seats/confirm", method: "POST", body: { paymentIntentId } }),
      invalidatesTags: ["Org", "OrgBilling", "OrgMe"],
    }),

    openBillingPortal: builder.mutation({
      query: () => ({ url: "/org/billing-portal", method: "POST" }),
    }),

    // Joining and leaving (any signed-in user).
    // { invites: [{ id, orgName, invitedByName, email, role, sentAt, expiresAt }] }
    getMyInvites: builder.query({
      query: () => ({ url: "/org/invites/mine", method: "GET" }),
      providesTags: ["OrgInvites"],
    }),
    acceptInvite: builder.mutation({
      query: (id) => ({ url: `/org/invites/${id}/accept`, method: "POST" }),
      invalidatesTags: ["OrgInvites", "OrgMe", "Org", "OrgBilling"],
    }),
    declineInvite: builder.mutation({
      query: (id) => ({ url: `/org/invites/${id}/decline`, method: "POST" }),
      invalidatesTags: ["OrgInvites"],
    }),
    leaveOrg: builder.mutation({
      query: () => ({ url: "/org/leave", method: "POST" }),
      invalidatesTags: ["OrgMe", "Org", "OrgBilling", "OrgInvites"],
    }),
  }),
});

export const {
  useGetMyMembershipQuery,
  useGetMyOrgQuery,
  useGetOrgBillingQuery,
  useLazyPreviewSeatsQuery,
  useCreateTeamSubscriptionSessionMutation,
  useCreateTeamTrialMutation,
  useGetOrgOverviewQuery,
  useGetOrgUsageQuery,
  useGetBillingSetupQuery,
  useAuthorizeBillingMutation,
  useConfirmBillingMutation,
  useCreateInviteMutation,
  useResendInviteMutation,
  useRevokeInviteMutation,
  useReinviteMutation,
  useHideInviteMutation,
  useChangeMemberRoleMutation,
  useRemoveMemberMutation,
  useUpdateSeatsMutation,
  useConfirmSeatPaymentMutation,
  useOpenBillingPortalMutation,
  useGetMyInvitesQuery,
  useAcceptInviteMutation,
  useDeclineInviteMutation,
  useLeaveOrgMutation,
} = orgApi;

/** A user-facing message for a failed org request. */
export const orgErrorMessage = (error, fallback = "Something went wrong. Please try again.") => {
  if (!error) return fallback;
  if (error.status === "FETCH_ERROR") return "Network error. Check your connection and try again.";
  return error.data?.message || error.data?.error || fallback;
};

/** The machine-readable code of a failed org request (e.g. "SEATS_FULL"). */
export const orgErrorCode = (error) => error?.data?.code || null;
